import { Link } from "eddev/routing"
import { ContentItemFragment, ResponsiveImageFragment } from "../../types.graphql"
import { cn } from "@utils/tw"
import { Themed, ThemeName } from "@components/atoms/Themed"
import { Button } from "@components/atoms/Button"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { useMemo } from "react"
import { usePageContext } from "../../contexts/PageContext"
import { IconArrowRight } from "@components/atoms/Icon"

export type ContentItem = {
  type: ContentItemFragment["__typename"]
  typeLabel: string
  title: string
  uri: string
  excerpt: string
  images: (ResponsiveImageFragment | null)[]
  categories: { name: string; uri: string }[]
  subtitle?: string | null
  podcastDuration?: string | null
  theme?: ThemeName | null
  hideImage?: boolean | null
}

export function useContentItem(item: ContentItemFragment): ContentItem {
  const theme = usePageContext().theme

  return useMemo(() => {
    switch (item.__typename) {
      case "Article":
        return {
          type: item.__typename!,
          typeLabel: "Article",
          title: item.title!,
          uri: item.uri!,
          excerpt: item.excerpt!,
          images: item.pageHeaderArticle?.featuredImages! || [],
          subtitle: item.pageHeaderArticle?.subtitle!,
          categories: item.pageHeaderArticle?.categories?.map((c) => ({ name: c?.name!, uri: c?.uri! })) || [],
          theme: "whiteLight", //item.pageTheme,
          hideImage: item.pageHeaderArticle?.hideFeaturedImage || false,
        }
      case "CaseStudy":
        return {
          type: item.__typename!,
          typeLabel: "Case Study",
          title: item.title!,
          uri: item.uri!,
          excerpt: item.excerpt!,
          images: item.pageHeaderCaseStudy?.featuredImages! || [],
          subtitle: item.pageHeaderCaseStudy?.subtitle!,
          categories: item.pageHeaderCaseStudy?.categories?.map((c) => ({ name: c?.name!, uri: c?.uri! })) || [],
          theme: item.pageTheme,
          hideImage: false,
        }
      case "Podcast":
        return {
          type: item.__typename!,
          typeLabel: "Podcast",
          title: item.title!,
          uri: item.uri!,
          excerpt: item.excerpt!,
          images: item.pageHeaderPodcast?.featuredImages! || [],
          subtitle: item.pageHeaderPodcast?.subtitle!,
          categories: item.pageHeaderPodcast?.categories?.map((c) => ({ name: c?.name!, uri: c?.uri! })) || [],
          podcastDuration: item.pageHeaderPodcast?.podcastDuration || null,
          theme: "whiteDark",
          hideImage: false,
        }
      case "WhitePaper":
        return {
          type: item.__typename!,
          typeLabel: "White Paper",
          title: item.title!,
          uri: item.uri!,
          excerpt: item.excerpt!,
          images: item.pageHeaderWhitepaper?.featuredImages! || [],
          subtitle: item.pageHeaderWhitepaper?.subtitle,
          categories: item.pageHeaderWhitepaper?.categories?.map((c) => ({ name: c?.name!, uri: c?.uri! })) || [],
          theme: item.pageTheme,
          hideImage: false,
        }
      default:
        throw new Error(`Unsupported content item type: ${item.__typename}`)
    }
  }, [item, theme])
}

export function ContentItemSlim(props: { item: ContentItemFragment; index?: number }) {
  const item = useContentItem(props.item)
  return (
    <Link
      href={item?.uri}
      className={cn(
        "w-full bg-blue p-7 flex justify-between md:gap-7 group",
        item.type === "Podcast" ? "rounded-xl" : "rounded-lg",
      )}
    >
      <div className="flex flex-col gap-4 w-full">
        <p className="type-label-s-caps">{item.typeLabel}</p>
        <p className="type-title-m">{item?.title}</p>
        {item?.subtitle && <p className="type-title-s">{item?.subtitle}</p>}
      </div>

      {!!item.podcastDuration && (
        <div className="type-label-m flex items-center gap-3 shrink-0 md:translate-x-[5.75em] md:group-hover:translate-x-0 transition-transform duration-500">
          <p className="type-label-m">{item.podcastDuration}</p>
          <svg className="w-7 h-7" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="1" width="34" height="34" rx="17" fill="#26231B" />
            <rect x="1" y="1" width="34" height="34" rx="17" stroke="#26231B" />
            <path
              d="M18.0006 20.9739C19.7916 20.9721 21.2429 19.5207 21.2448 17.7298V13.4043C21.2437 10.907 18.5395 9.3473 16.3773 10.5969C15.3747 11.1764 14.757 12.2463 14.7565 13.4043V17.7298C14.7584 19.5207 16.2097 20.9721 18.0006 20.9739ZM15.8379 13.4043C15.8379 11.7394 17.6402 10.6988 19.082 11.5313C19.7512 11.9176 20.1634 12.6316 20.1634 13.4043V17.7298C20.1634 19.3947 18.3611 20.4353 16.9193 19.6028C16.2501 19.2165 15.8379 18.5025 15.8379 17.7298V13.4043ZM18.5413 23.1097V25.2995C18.5413 25.7157 18.0908 25.9758 17.7303 25.7677C17.563 25.6711 17.46 25.4926 17.46 25.2995V23.1097C14.6987 22.8287 12.5972 20.5053 12.5938 17.7298C12.5938 17.3136 13.0443 17.0534 13.4048 17.2616C13.5721 17.3581 13.6751 17.5366 13.6751 17.7298C13.6764 21.0596 17.2818 23.1393 20.1648 21.4733C21.5018 20.7007 22.3256 19.274 22.3262 17.7298C22.3262 17.3136 22.7767 17.0534 23.1372 17.2616C23.3045 17.3581 23.4075 17.5366 23.4075 17.7298C23.4041 20.5053 21.3025 22.8287 18.5413 23.1097Z"
              fill="#E5E8DB"
            />
          </svg>
        </div>
      )}

      <div className="hidden md:flex flex-col justify-center md:col-span-1 transition-opacity duration-500 opacity-0 group-hover:opacity-100 self-center">
        <svg className="w-7 h-7" viewBox="0 0 37 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="0.5" width="35" height="34" rx="17" stroke="currentColor" />
          <path
            d="M17.662 21.94L20.91 18.678H13.686V17.53H20.91L17.662 14.268H19.342L23.066 18.104L19.342 21.94H17.662Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </Link>
  )
}

export function ContentItemDetailed(props: { item: ContentItemFragment }) {
  const item = useContentItem(props.item)
  return item.images.length > 0 ? (
    <ContentItemDetailedFull item={props.item} />
  ) : (
    <ContentItemDetailedSlim item={props.item} />
  )
}

export function ContentItemDetailedFull(props: { item: ContentItemFragment }) {
  const item = useContentItem(props.item)
  return (
    <Themed
      theme={item.theme && item.theme !== "none" ? item.theme : "whiteLight"}
      className={cn(
        "w-full p-7 grid grid-cols-1 md:grid-cols-12 gap-7 themed-bg themed-fg rounded-lg group",
        item.type === "Podcast" && "rounded-xl",
      )}
    >
      <div className="flex flex-col gap-7 self-stretch justify-between md:col-span-2">
        <div className="flex flex-col gap-4">
          {item.categories?.map(
            (category, index) =>
              !!category && (
                <Button key={index} href={category.uri || ""} size="sm">
                  {category.name}
                </Button>
              ),
          )}
        </div>
        {!!item.podcastDuration && (
          <div className="flex items-center gap-3 shrink-0">
            <svg className="w-7 h-7" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="34" height="34" rx="17" fill="#26231B" />
              <rect x="1" y="1" width="34" height="34" rx="17" stroke="#26231B" />
              <path
                d="M18.0006 20.9739C19.7916 20.9721 21.2429 19.5207 21.2448 17.7298V13.4043C21.2437 10.907 18.5395 9.3473 16.3773 10.5969C15.3747 11.1764 14.757 12.2463 14.7565 13.4043V17.7298C14.7584 19.5207 16.2097 20.9721 18.0006 20.9739ZM15.8379 13.4043C15.8379 11.7394 17.6402 10.6988 19.082 11.5313C19.7512 11.9176 20.1634 12.6316 20.1634 13.4043V17.7298C20.1634 19.3947 18.3611 20.4353 16.9193 19.6028C16.2501 19.2165 15.8379 18.5025 15.8379 17.7298V13.4043ZM18.5413 23.1097V25.2995C18.5413 25.7157 18.0908 25.9758 17.7303 25.7677C17.563 25.6711 17.46 25.4926 17.46 25.2995V23.1097C14.6987 22.8287 12.5972 20.5053 12.5938 17.7298C12.5938 17.3136 13.0443 17.0534 13.4048 17.2616C13.5721 17.3581 13.6751 17.5366 13.6751 17.7298C13.6764 21.0596 17.2818 23.1393 20.1648 21.4733C21.5018 20.7007 22.3256 19.274 22.3262 17.7298C22.3262 17.3136 22.7767 17.0534 23.1372 17.2616C23.3045 17.3581 23.4075 17.5366 23.4075 17.7298C23.4041 20.5053 21.3025 22.8287 18.5413 23.1097Z"
                fill="#E5E8DB"
              />
            </svg>
            <p className="type-label-m">{item.podcastDuration}</p>
          </div>
        )}
      </div>
      <Link href={item.uri} className="flex flex-col gap-4 w-full md:col-span-4">
        <p className="type-label-s-caps">{item.typeLabel}</p>
        <p className="type-title-m">{item?.title}</p>
        {item?.subtitle && <p className="type-title-s">{item?.subtitle}</p>}
        {item.excerpt && (
          <div className="type-body-m text-pretty" dangerouslySetInnerHTML={{ __html: item?.excerpt || "" }} />
        )}
      </Link>
      {!item.hideImage && (
        <Link href={item.uri} className="flex flex-col gap-4 w-full h-full self-stretch md:col-span-5">
          {item.images?.[0]?.mediaItemUrl && (
            <ResponsiveImage
              {...item.images[0]}
              className="w-auto h-selectedContentImageH rounded-full overflow-hidden"
              objectFit="cover"
            />
          )}
        </Link>
      )}
      <div className="hidden md:flex flex-col justify-center self-stretch h-full md:col-span-1 transition-opacity duration-500 opacity-0 group-hover:opacity-100">
        <svg className="w-7 h-7" viewBox="0 0 37 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="0.5" width="35" height="34" rx="17" stroke="currentColor" />
          <path
            d="M17.662 21.94L20.91 18.678H13.686V17.53H20.91L17.662 14.268H19.342L23.066 18.104L19.342 21.94H17.662Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </Themed>
  )
}

export function ContentItemDetailedSlim(props: { item: ContentItemFragment }) {
  const item = useContentItem(props.item)
  return (
    <Themed
      theme={item.theme && item.theme !== "none" ? item.theme : "whiteLight"}
      className={cn(
        "w-full p-7 grid grid-cols-1 md:grid-cols-12 gap-7 themed-bg themed-fg rounded-lg group",
        item.type === "Podcast" && "rounded-xl",
      )}
    >
      <div className="flex flex-col gap-7 self-stretch justify-between md:col-span-2">
        <div className="flex flex-col gap-4">
          {item.categories?.map(
            (category, index) =>
              !!category && (
                <Button key={index} href={category.uri || ""} size="sm">
                  {category.name}
                </Button>
              ),
          )}
        </div>
        {!!item.podcastDuration && (
          <div className="flex items-center gap-3 shrink-0">
            <svg className="w-7 h-7" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="34" height="34" rx="17" fill="#26231B" />
              <rect x="1" y="1" width="34" height="34" rx="17" stroke="#26231B" />
              <path
                d="M18.0006 20.9739C19.7916 20.9721 21.2429 19.5207 21.2448 17.7298V13.4043C21.2437 10.907 18.5395 9.3473 16.3773 10.5969C15.3747 11.1764 14.757 12.2463 14.7565 13.4043V17.7298C14.7584 19.5207 16.2097 20.9721 18.0006 20.9739ZM15.8379 13.4043C15.8379 11.7394 17.6402 10.6988 19.082 11.5313C19.7512 11.9176 20.1634 12.6316 20.1634 13.4043V17.7298C20.1634 19.3947 18.3611 20.4353 16.9193 19.6028C16.2501 19.2165 15.8379 18.5025 15.8379 17.7298V13.4043ZM18.5413 23.1097V25.2995C18.5413 25.7157 18.0908 25.9758 17.7303 25.7677C17.563 25.6711 17.46 25.4926 17.46 25.2995V23.1097C14.6987 22.8287 12.5972 20.5053 12.5938 17.7298C12.5938 17.3136 13.0443 17.0534 13.4048 17.2616C13.5721 17.3581 13.6751 17.5366 13.6751 17.7298C13.6764 21.0596 17.2818 23.1393 20.1648 21.4733C21.5018 20.7007 22.3256 19.274 22.3262 17.7298C22.3262 17.3136 22.7767 17.0534 23.1372 17.2616C23.3045 17.3581 23.4075 17.5366 23.4075 17.7298C23.4041 20.5053 21.3025 22.8287 18.5413 23.1097Z"
                fill="#E5E8DB"
              />
            </svg>
            <p className="type-label-m">{item.podcastDuration}</p>
          </div>
        )}
      </div>
      <Link href={item.uri} className="flex flex-col gap-4 w-full md:col-span-4">
        <p className="type-label-s-caps">{item.typeLabel}</p>
        <p className="type-title-m">{item?.title}</p>
        {item?.subtitle && <p className="type-title-s">{item?.subtitle}</p>}
      </Link>
      <Link href={item.uri} className="flex flex-col gap-4 w-full md:col-span-5">
        {item.excerpt && (
          <div className="type-body-m text-pretty" dangerouslySetInnerHTML={{ __html: item?.excerpt || "" }} />
        )}
      </Link>
      <div className="hidden md:flex flex-col justify-center self-stretch h-full md:col-span-1 transition-opacity duration-500 opacity-0 group-hover:opacity-100">
        <svg className="w-7 h-7" viewBox="0 0 37 35" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="0.5" width="35" height="34" rx="17" stroke="currentColor" />
          <path
            d="M17.662 21.94L20.91 18.678H13.686V17.53H20.91L17.662 14.268H19.342L23.066 18.104L19.342 21.94H17.662Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </Themed>
  )
}

export function ContentItemSquare(props: { item: ContentItemFragment }) {
  const item = useContentItem(props.item)
  return (
    <div className="w-full group">
      <Themed
        theme={item.theme && item.theme !== "none" ? item.theme : "whiteLight"}
        className={cn(
          "w-full p-7 themed-fg relative transition-all duration-300 md:hover:pb-[calc(var(--spacing-7)+3.5em)] overflow-hidden",
        )}
      >
        <div
          className={cn(
            "absolute top-0 left-0 w-full h-full themed-bg transition-all duration-300 rounded-lg pointer-events-none md:group-hover:rounded-xl",
            item.type === "Podcast" && "rounded-xl",
          )}
        />
        <div className="flex flex-col gap-7 relative">
          {item.categories && (item.categories.length > 0 || item.podcastDuration) && (
            <div className="w-full flex flex-row gap-7 self-stretch justify-between relative">
              <div className="flex gap-1">
                {item.categories?.map(
                  (category, index) =>
                    !!category && (
                      <Button key={index} href={category.uri || ""} size="sm">
                        {category.name}
                      </Button>
                    ),
                )}
              </div>
              {item.podcastDuration && (
                <div className="flex flex-col items-center gap-3 shrink-0 absolute right-0 top-0">
                  <svg className="w-7 h-7" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="34" height="34" rx="17" fill="#26231B" />
                    <rect x="1" y="1" width="34" height="34" rx="17" stroke="#26231B" />
                    <path
                      d="M18.0006 20.9739C19.7916 20.9721 21.2429 19.5207 21.2448 17.7298V13.4043C21.2437 10.907 18.5395 9.3473 16.3773 10.5969C15.3747 11.1764 14.757 12.2463 14.7565 13.4043V17.7298C14.7584 19.5207 16.2097 20.9721 18.0006 20.9739ZM15.8379 13.4043C15.8379 11.7394 17.6402 10.6988 19.082 11.5313C19.7512 11.9176 20.1634 12.6316 20.1634 13.4043V17.7298C20.1634 19.3947 18.3611 20.4353 16.9193 19.6028C16.2501 19.2165 15.8379 18.5025 15.8379 17.7298V13.4043ZM18.5413 23.1097V25.2995C18.5413 25.7157 18.0908 25.9758 17.7303 25.7677C17.563 25.6711 17.46 25.4926 17.46 25.2995V23.1097C14.6987 22.8287 12.5972 20.5053 12.5938 17.7298C12.5938 17.3136 13.0443 17.0534 13.4048 17.2616C13.5721 17.3581 13.6751 17.5366 13.6751 17.7298C13.6764 21.0596 17.2818 23.1393 20.1648 21.4733C21.5018 20.7007 22.3256 19.274 22.3262 17.7298C22.3262 17.3136 22.7767 17.0534 23.1372 17.2616C23.3045 17.3581 23.4075 17.5366 23.4075 17.7298C23.4041 20.5053 21.3025 22.8287 18.5413 23.1097Z"
                      fill="#E5E8DB"
                    />
                  </svg>
                  <p className="type-label-m">{item.podcastDuration}</p>
                </div>
              )}
            </div>
          )}
          <Link href={item.uri} className="flex flex-col gap-7 md:gap-5 w-full">
            <div className="flex flex-col gap-4 md:gap-4">
              <p className="type-label-s-caps">{item.typeLabel}</p>
              <p className="type-title-m">{item?.title}</p>
              {item?.subtitle && <p className="type-title-s">{item?.subtitle}</p>}
            </div>
            {!item.hideImage && item.images?.[0]?.mediaItemUrl && (
              <ResponsiveImage
                {...item.images[0]}
                className="w-auto h-selectedContentImageH rounded-lg overflow-hidden"
                objectFit="cover"
              />
            )}
            {item.excerpt && item.excerpt !== "" && (
              <div className="type-body-m text-pretty" dangerouslySetInnerHTML={{ __html: item?.excerpt || "" }} />
            )}
          </Link>
          <Button
            radius="sm"
            iconAlign="right"
            icon={<IconArrowRight className="ml-3 w-[1em] h-[1em]" />}
            outerClassName="md:absolute md:left-0 md:-bottom-[calc(var(--spacing-7)+1.5em)] md:group-hover:opacity-100 md:group-hover:pointer-events-auto md:opacity-0 md:pointer-events-none transition-all duration-300"
            href={item.uri}
          >
            Read More
          </Button>
        </div>
      </Themed>
    </div>
  )
}
